import { parseLocationParts, resolveLocationTimezone } from '../../domain/locationTimezone.js';
import { chatComplete, deepSeekReady } from '../../llm/client.js';
import { parseJsonLoose } from '../../llm/prompts.js';

const MIN_LOCATION_CHARS = 2;

const LOCATION_TIMEZONE_PROMPT = (raw: string) =>
  `Return JSON only: {"location":"City, Region, Country","timezone":"IANA id"}.
The city, the region, or the country may be misspelled or missing.
If a part is misspelled, correct that spelling. Example: "Vancuver, Britsh Columbia, Cannada" → "Vancouver, British Columbia, Canada".
If a part is missing, fill in the real city, region, and country. Example: "Nuuk" → "Nuuk, Sermersooq, Greenland".
timezone is the IANA zone local clocks use there. A new city the map has never heard of still needs a real IANA id (America/Nuuk is a valid example).
Exactly three comma-separated parts for location.
Location: ${raw}`;

export interface LocationTimezoneReply {
  location: string;
  timezone?: string;
}

export interface LocationTimezoneResolution {
  locationForMap: string;
  correctedLocation?: string;
  timezone?: string;
}

/** True when trimmed input is worth sending to DeepSeek. */
export function shouldResolveLocationTimezone(raw: string): boolean {
  const trimmed = raw.trim();
  return trimmed.length >= MIN_LOCATION_CHARS;
}

/** Accept IANA Region/City ids Intl recognizes; reject abbreviations like PST. */
export function isValidIanaTimezone(id: string): boolean {
  const trimmed = id.trim();
  if (!trimmed) return false;
  if (!trimmed.includes('/') && trimmed !== 'UTC' && trimmed !== 'GMT') return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

/** Parse a DeepSeek location+timezone reply; null when location shape is invalid. */
export function parseLocationTimezoneReply(text: string): LocationTimezoneReply | null {
  try {
    const parsed = parseJsonLoose(text) as { location?: unknown; timezone?: unknown };
    if (typeof parsed.location !== 'string') return null;
    const location = parsed.location.trim();
    if (!parseLocationParts(location)) return null;
    const timezone =
      typeof parsed.timezone === 'string' && parsed.timezone.trim()
        ? parsed.timezone.trim()
        : undefined;
    return { location, timezone };
  } catch {
    return null;
  }
}

/** Map wins when known; otherwise use a validated DeepSeek timezone. */
export function resolveTimezoneForLocation(
  locationForMap: string,
  deepSeekTimezone?: string | null,
): string | undefined {
  const mapTz = resolveLocationTimezone(locationForMap);
  if (mapTz) return mapTz;
  if (deepSeekTimezone && isValidIanaTimezone(deepSeekTimezone)) {
    return deepSeekTimezone.trim();
  }
  return undefined;
}

/** Ask DeepSeek for corrected location and timezone; null when unavailable or unparseable. */
export async function fetchLocationTimezone(raw: string): Promise<LocationTimezoneResolution | null> {
  const trimmed = raw.trim();
  if (!shouldResolveLocationTimezone(trimmed) || !deepSeekReady()) return null;
  try {
    const text = await chatComplete(
      [{ role: 'user', content: LOCATION_TIMEZONE_PROMPT(trimmed) }],
      { json: true, maxTokens: 160, temperature: 0 },
    );
    const parsed = parseLocationTimezoneReply(text);
    if (!parsed) return null;
    const locationForMap = parsed.location;
    return {
      locationForMap,
      correctedLocation: locationForMap !== trimmed ? locationForMap : undefined,
      timezone: resolveTimezoneForLocation(locationForMap, parsed.timezone),
    };
  } catch {
    return null;
  }
}
